import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import InvestorPortalLayout from '../../../components/market/InvestorPortalLayout';
import { useInvestorAuth } from '../../../context/InvestorAuthContext';
import { Icon } from '../../../utils/icons';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const formatDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatFull = (s) => s ? new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

export default function InvestorMessages() {
  const { token } = useInvestorAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMessages = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/market/investor/messages`, { headers });
      setMessages(res.data?.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const markRead = async (id) => {
    setMessages((prev) => prev.map((m) => m.id === id ? { ...m, is_read: true } : m));
    await axios.patch(`${API_URL}/api/market/investor/messages/${id}/read`, {}, { headers }).catch(() => {});
  };

  const markAllRead = async () => {
    setMessages((prev) => prev.map((m) => ({ ...m, is_read: true })));
    await axios.patch(`${API_URL}/api/market/investor/messages/read-all`, {}, { headers }).catch(() => {});
  };

  const handleSelect = (m) => {
    setSelectedId(m.id);
    if (!m.is_read && m.sender === 'doorly') markRead(m.id);
  };

  const selected = messages.find((m) => m.id === selectedId);
  const unreadCount = messages.filter((m) => !m.is_read && m.sender === 'doorly').length;

  return (
    <InvestorPortalLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111111' }}>Messages</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              {unreadCount > 0 ? `${unreadCount} unread · ${messages.length} total` : `${messages.length} message${messages.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {unreadCount > 0 && (
              <button onClick={markAllRead}
                style={{ padding: '8px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Mark all read
              </button>
            )}
            <button onClick={() => setComposeOpen(true)}
              style={{ padding: '8px 16px', background: '#3a6ad9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#fff' }}>
              + New Message
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 }}>
            Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 12px', borderRadius: 12, background: '#eff3fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="chat" size={26} color="#3a6ad9" />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111111', marginBottom: 4 }}>No messages yet.</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Our team will be in touch soon.</div>
          </div>
        ) : (
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16, alignItems: 'flex-start' }}>
            {/* List */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
              {messages.map((m) => {
                const isSelected = m.id === selectedId;
                const isUnread = !m.is_read && m.sender === 'doorly';
                return (
                  <div key={m.id} onClick={() => handleSelect(m)}
                    style={{
                      padding: '14px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                      background: isSelected ? '#eff3fe' : isUnread ? '#fafcff' : '#fff',
                      borderLeft: isSelected ? '3px solid #3a6ad9' : '3px solid transparent',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = isUnread ? '#fafcff' : '#fff'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      {isUnread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3a6ad9', flexShrink: 0 }} />}
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                        {m.sender === 'investor' ? 'You' : (m.sender_name || 'Doorly Team')}
                      </div>
                      <div style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af' }}>{formatDate(m.created_at)}</div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: isUnread ? 700 : 500, color: '#111111', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.subject}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.body}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Detail */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, minHeight: 400 }}>
              {selected ? (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
                    {selected.sender === 'investor' ? 'You sent' : 'From Doorly'}
                  </div>
                  <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: '#111111' }}>{selected.subject}</h2>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 18 }}>
                    {selected.sender_name || (selected.sender === 'investor' ? 'You' : 'Doorly Team')} · {formatFull(selected.created_at)}
                  </div>
                  <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {selected.body}
                  </div>
                  <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid #f3f4f6' }}>
                    <button onClick={() => setComposeOpen(true)}
                      style={{ padding: '8px 16px', background: '#3a6ad9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#fff' }}>
                      Reply
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ padding: 80, textAlign: 'center', color: '#9ca3af' }}>
                  Select a message to read.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {composeOpen && (
        <ComposeModal
          token={token}
          defaultSubject={selected ? `Re: ${selected.subject}` : ''}
          onClose={() => setComposeOpen(false)}
          onSent={(m) => {
            setComposeOpen(false);
            setMessages((prev) => [m, ...prev]);
            setSelectedId(m.id);
          }}
        />
      )}
    </InvestorPortalLayout>
  );
}

function ComposeModal({ token, onClose, onSent, defaultSubject }) {
  const [subject, setSubject] = useState(defaultSubject || '');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setError('Subject and message body are required.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/api/market/investor/messages`,
        { subject: subject.trim(), body: body.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSent(res.data?.message || { id: Date.now(), subject, body, sender: 'investor', created_at: new Date().toISOString(), is_read: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, maxWidth: 600, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#111111' }}>New Message</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto' }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What's this about?"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: '#111111', boxSizing: 'border-box', outline: 'none' }}
              onFocus={(e) => (e.target.style.borderColor = '#3a6ad9')}
              onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Message</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message…"
              rows={8}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: '#111111', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
              onFocus={(e) => (e.target.style.borderColor = '#3a6ad9')}
              onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')} />
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Your message will be sent to the Doorly investor team at investors@godoorly.com.
          </div>
          {error && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff5f6', border: '1px solid #ffd6db', borderRadius: 8, color: '#ff082f', fontSize: 13 }}>
              {error}
            </div>
          )}
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={sending}
            style={{ padding: '9px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
            Cancel
          </button>
          <button onClick={handleSend} disabled={sending || !subject.trim() || !body.trim()}
            style={{ padding: '9px 18px', background: '#3a6ad9', border: 'none', borderRadius: 8, cursor: sending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, color: '#fff', opacity: sending || !subject.trim() || !body.trim() ? 0.5 : 1 }}>
            {sending ? 'Sending…' : 'Send Message'}
          </button>
        </div>
      </div>
    </div>
  );
}
